import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';

import getAllYouthListQuery from '../../pages/Administer/queries/getAllYouthList.graphql';
import getAllPlansListQuery from '../../pages/Administer/queries/getAllPlansList.graphql';
import getAdministeredPlansQuery from '../../pages/Administer/queries/getAdministeredPlans.graphql';

import { isDateInPast } from '../../pages/Administer/helpers';
import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from '../../pages/Administer/ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

const USER = window.cccisd && window.cccisd.fortress.user.acting;

const initialState = {
    loading: true,
    youthList: [],
    plansList: {},
    youthPlans: {},
};

// query list of all youth belonging to this current Care Coordinator
export const queryAdministerYouthList = createAsyncThunk('administer/queryYouthList', async () => {
    const response = await client.query({
        query: getAllYouthListQuery,
        fetchPolicy: 'network-only',
        variables: { careCoordinatorPawnId: USER.id },
    });

    return _get(response, 'data.roles.youthList', []).map(youth => {
        const fields = { ...youth.fields };
        fields.pawnId = youth.pawn.pawnId;
        fields.caregiverList = youth.parentRoles.caregiverList;
        fields.flags = [];

        const youthPlanDeploymentIds = {};
        youth.selectedAssignmentPlanList.forEach(plan => {
            youthPlanDeploymentIds[plan.assignmentPlanId] = plan.sessionList.map(session => session.sessionId);
        });

        youth.flags.forEach(flag => {
            flag.flagProgressList.forEach(progress => {
                fields.flags.push({
                    key: `${youth.pawn.pawnId}_${progress.flagId}`,
                    respondent_pawn_id: youth.pawn.pawnId,
                    critical: progress.flag.name.toLowerCase().includes('critical'),
                    surveyItem: progress.flag.description,
                    response: progress.flag.equation,
                    date: progress.flaggedDate,
                    respondent: 'youth',
                    actions: ['resolve', 'addNote', 'viewLogs'],
                    log: progress.log,
                    notes: progress.notes,
                    flagId: progress.flagId,
                    status: progress.status,
                    unflaggedReason: progress.unflaggedReason,
                    flagProgressId: progress.flagProgressId,
                    unflaggedAt: progress.unflaggedAt,
                    wrapId: youth.fields.wrapId,
                });
            });
        });

        // add the caregiver flags to the flags object
        youth.parentRoles.caregiverList.forEach(caregiver => {
            caregiver.flags.forEach(flag => {
                if (!Object.values(youthPlanDeploymentIds).flat().includes(flag.sessionId)) return;

                flag.flagProgressList.forEach(progress => {
                    fields.flags.push({
                        key: `${caregiver.pawn.pawnId}_${progress.flagId}`,
                        respondent_pawn_id: caregiver.pawn.pawnId,
                        critical: progress.flag.name.toLowerCase().includes('critical'),
                        surveyItem: progress.flag.description,
                        response: progress.flag.equation,
                        date: progress.flaggedDate,
                        respondent: 'caregiver',
                        caregiverName: `${caregiver.fields.firstName} ${caregiver.fields.lastName}`,
                        actions: ['resolve', 'addNote', 'viewLogs'],
                        log: progress.log,
                        notes: progress.notes,
                        flagId: progress.flagId,
                        status: progress.status,
                        unflaggedReason: progress.unflaggedReason,
                        flagProgressId: progress.flagProgressId,
                        unflaggedAt: progress.unflaggedAt,
                        wrapId: caregiver.fields.wrapId,
                    });
                });
            });
        });

        return fields;
    });
});

// Plans are queried by custom assignment handle
// Example: assignmentHandle: [
//     "onboarding_youth"
//     "onboarding_caregiver"
//     "22_washington_youth" // 22 = group_id of the careCoordinator
//     "22_washington_caregiver"]
// Each surevey within an assignment is one week of questions
// Each assignment is deployed by itself for use by caregivers or youth
// The name that appears on the list of plans in the "Add Plan" Modal is based off of the deploymentHandle
export const queryAdministerPlansList = createAsyncThunk('administer/queryPlansList', async () => {
    const groupId = (USER.group_id || '-1').toString();
    const assignmentHandles = [
        ..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, 'onboarding.handles', []),
        ..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, `${groupId}.handles`, []), // custom state plans
    ];

    const response = await client.query({
        query: getAllPlansListQuery,
        variables: { assignment_handles: assignmentHandles },
        fetchPolicy: 'network-only',
    });

    const plans = {};
    _get(response, 'data.flows.assignmentList', []).forEach(assignment => {
        const { assignmentHandle } = assignment;

        // list of main parent deployments for each plan (weekly)
        const deploymentHandles = assignment.deploymentList.filter(
            deployment =>
                deployment.timepoint === null && deployment.parentDeploymentId === null && deployment.deploymentHandle
        );

        if (deploymentHandles.length === 0) return;

        const planName = deploymentHandles[0].deploymentHandle.split('_')[0].trim().toUpperCase();

        if (!planName) return;

        if (!plans[planName]) {
            plans[planName] = {
                planName,
                youthDeploymentHandles: [],
                caregiverDeploymentHandles: [],
                assignments: [],
                isOrgPlan: assignmentHandle.startsWith(groupId),
            };
        }

        if (deploymentHandles[0].deploymentHandle.includes('youth')) {
            plans[planName].youthDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
        } else if (deploymentHandles[0].deploymentHandle.includes('caregiver')) {
            plans[planName].caregiverDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
        }

        const surveys = _get(assignment, 'surveyList', []).map(survey => {
            const info = { surveyLabel: survey.label, surveyQuestions: [] };
            _get(survey, 'flowList', []).forEach(flow => {
                _get(flow, 'entityList', []).forEach(entity => {
                    if (_get(entity, 'content.question', false)) {
                        info.surveyQuestions.push(entity.content.question);
                    }
                });
            });
            return info;
        });

        plans[planName].assignments.push({ assignmentLabel: assignment.label, surveys });
    });

    return plans;
});

// query list of all administered plans
export const queryAdministerYouthPlans = createAsyncThunk('administer/queryYouthPlans', async () => {
    const response = await client.query({
        query: getAdministeredPlansQuery,
        fetchPolicy: 'network-only',
        variables: { careCoordinatorPawnId: USER.id },
    });

    const youthPlans = {};
    _get(response, 'data.roles.youthList', []).forEach(youth => {
        const { pawnId } = youth.pawn;
        const plans = youth.selectedAssignmentPlanList;

        plans.forEach(plan => {
            const settings = JSON.parse(plan.settings);
            plan.isStopped = _get(settings, 'stopPlan', false);
            plan.hasNotOpenedYet = !isDateInPast(settings.startDate);
            plan.isClosed =
                plan.sessionList.filter(s => s.deployment.isOpen || !isDateInPast(s.opensDate)).length === 0;
            plan.wrapIds = [
                { pawnId, wrapId: youth.fields.wrapId },
                ...youth.parentRoles.caregiverList.map(cg => ({
                    pawnId: cg.pawn.pawnId,
                    wrapId: cg.fields.wrapId,
                })),
            ];
        });

        youthPlans[pawnId] = plans;
    });

    return youthPlans;
});

const administerSlice = createSlice({
    name: 'administer',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(queryAdministerYouthList.pending, state => {
                state.loading = true;
            })
            .addCase(queryAdministerYouthList.fulfilled, (state, action) => {
                state.youthList = action.payload;
                state.loading = false;
            })
            .addCase(queryAdministerPlansList.fulfilled, (state, action) => {
                state.plansList = action.payload;
            })
            .addCase(queryAdministerYouthPlans.fulfilled, (state, action) => {
                state.youthPlans = action.payload;
            });
    },
});

export default administerSlice.reducer;
